import { AfterViewInit, Component, Injector, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthRepository } from '@timechef/core/auth';
import { ElfDataSource } from '@timechef/core/utils/elf-utils';
import { Observable, Subject, filter } from 'rxjs';
import { Manipulation, Navigation, Pagination, default as Swiper, default as SwiperCore } from 'swiper';
import { SwiperComponent } from 'swiper/angular';
import { Order, PageInfoWithData } from '../../../state/history.models';
import { HistoryRepository } from '../../../state/history.repository';
import { HistoryService } from '../../../state/history.service';

SwiperCore.use([Navigation, Pagination, Manipulation]);
const REVERSE_ARROW_AT = 100;

@UntilDestroy()
@Component({
  selector: 'app-orders-modal',
  templateUrl: './orders-modal.component.html',
  styleUrls: ['./orders-modal.component.scss'],
  standalone: false,
})
export class OrdersModalComponent implements OnInit, AfterViewInit {
  @Input() orderId?: number;
  @Input() options: OrdersModalOption = { openSingleAsStandalone: false };

  @ViewChild(SwiperComponent) swiper?: SwiperComponent;
  orders?: PageInfoWithData<Order>;

  orderDS$?: Observable<ElfDataSource<Order | null>>;

  initialSlide = 0;
  hasSetInitialSlide = false;

  userFullName: string;

  swipeXComplete = new Subject<number>();
  reverseArrow = false;

  private historyService: HistoryService;

  constructor(
    private modalCtrl: ModalController,
    private historyRepo: HistoryRepository,
    injector: Injector,
    private zone: NgZone,
  ) {
    this.historyService = injector.get(HistoryService);
    this.userFullName = injector.get(AuthRepository).getUserFullName();
  }

  ngOnInit() {
    if (this.options?.openSingleAsStandalone) {
      // open single
      this.orderDS$ = this.historyRepo.standaloneOrderDS.data$();
      this.historyService.getStandaloneOrder(this.orderId!).subscribe();
    } else {
      this.historyRepo.ordersPageInfoWithData$.pipe(untilDestroyed(this)).subscribe((data) => {
        this.zone.run(() => {
          this.orders = data;
          if (!this.orderId || this.hasSetInitialSlide) return;

          this.initialSlide = this.orders.data.findIndex((r) => r.id === this.orderId);
          this.hasSetInitialSlide = true;
          this.swiper?.swiperRef.update();
        });

        this.swiper?.swiperRef.update();
      });

      // used to
      this.swipeXComplete
        .pipe(
          filter((_x) => this.orders!.hasMore),
          untilDestroyed(this),
        )
        .subscribe((_x) => {
          if (this.reverseArrow) {
            this.zone.run(() => {
              this.reverseArrow = false;
            });

            if (this.historyRepo.getOrdersRequestStatus().value !== 'pending')
              this.historyService.getOrders({ pageIndex: this.orders!.currentPageIndex + 1 }).subscribe();
          }
        });
    }
  }

  async orderCancelled() {
    await this.dismissModal('cancel');
  }

  async dismissModal(result?: OrdersModalComponentResult) {
    await this.modalCtrl.dismiss(result);
  }

  ngAfterViewInit() {
    if (this.swiper) {
      this.swiper.s_sliderMove.subscribe(([swiper, _ev]) => {
        const computeSwiperX = this.computeSwiperX(swiper);

        const rev = computeSwiperX > REVERSE_ARROW_AT;
        if (this.reverseArrow !== rev) {
          this.zone.run(() => {
            this.reverseArrow = rev;
          });
        }
      });

      this.swiper.s_touchEnd.subscribe(([swiper, _ev]) => {
        const computeSwiperX = this.computeSwiperX(swiper);
        this.swipeXComplete.next(computeSwiperX);
      });
    }
  }

  private computeSwiperX(swiper: Swiper) {
    const internalSwiper = swiper as any;
    const endX = internalSwiper.virtualSize - internalSwiper.slidesSizesGrid[0];
    return -(endX - (Math.abs(swiper.translate) + 20));
  }

  transformtValue(value: number) {
    return Math.abs(value);
  }
}

export interface OrdersModalOption {
  openSingleAsStandalone: boolean;
}

export type OrdersModalComponentResult = 'cancel';
